<template>
    <section>
        <PostsFilters @loadpage="loadpage" :formTypeId="formTypeId" />
        <div v-if="(isLoadingPosts && loading) || (posts && posts.data !== undefined)">
            <BmvTable
                :columns="tableColumns"
                :data="posts && posts.data ? posts.data : []"
                :loading="!loading"
                :pagination="true"
                :total-results="posts.total || 0"
                :total-pages="posts.total ? Math.ceil(posts.total / posts.per_page) : 0"
                :current-page="posts.current_page || 1"
                :per-page="posts.per_page || 20"
                :sort-field="config.sort"
                :sort-direction="config.sortDirection"
                @sort="onSort"
                @page-change="onPageChange"
            >
                <!-- Reference -->
                <template #cell-id="{ row }">
                    {{ row.reference }}
                </template>

                <!-- Form name -->
                <template v-slot:[formNameSlot]="{ row }">
                    {{ row.form ? row.form.name : '' }}
                </template>

                <!-- Status -->
                <template #cell-status_id="{ row }">
                    <ButtonEditStatus variant="badge" :post="row" />
                </template>

                <!-- Title -->
                <template #cell-title="{ row }">
                    <router-link
                        :to="`/posts/${row.id}`"
                        class="bmv-cell-link"
                    >
                        {{ row.title || '—' }}
                    </router-link>
                </template>

                <!-- Date -->
                <template #cell-created_at="{ row }">
                    {{ moment(row.created_at).format('L') }}
                </template>

                <!-- Operator -->
                <template #cell-operator="{ row }">
                    <div v-if="row.racis" class="tp-operators">
                        <router-link
                            v-for="raci in row.racis.filter(r => r.is_responsible == 1)"
                            :key="raci.id"
                            :to="`/company/user/${raci.id}/profile`"
                            class="tp-operator-badge tp-operator-link"
                        >
                            <i class="fal fa-user"></i>
                            {{ raci.name }}
                        </router-link>
                    </div>
                </template>

                <!-- Priority -->
                <template #cell-priority="{ row }">
                    <ButtonEditPriority :post="row" />
                </template>

                <!-- Actions -->
                <template #cell-actions="{ row }">
                    <div class="d-flex align-items-center" style="gap: 6px;">
                        <button
                            @click.prevent="showPostPreview(row)"
                            class="bmv-action-btn bmv-action-btn--view"
                        >
                            <i class="fal fa-eye" style="color: #fff !important;"></i>
                        </button>
                        <ButtonRemind variant="minimal" :post="row" />
                    </div>
                </template>
            </BmvTable>

            <PostPreviewModal ref="postPreview" :post="setSelectedPost" />
        </div>
    </section>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import BmvTable from '@/components/UI/BmvTable'
import PostPreviewModal from '@/components/Model/Post/PostPreviewModal'
import PostsFilters from '@/components/Model/Post/PostsFilters'
import ButtonEditStatus from '@/components/UI/ButtonEditStatus'
import ButtonRemind from '@/components/UI/ButtonRemind'
import ButtonEditPriority from './ButtonEditPriority'

export default {
    name: 'TabPosts',
    props: ['isLoadingPosts', 'formTypeId'],
    components: {
        BmvTable,
        PostPreviewModal,
        ButtonRemind,
        PostsFilters,
        ButtonEditStatus,
        ButtonEditPriority
    },
    data: function () {
        return {
            setSelectedPost: {},
            loading: false,
            formNameSlot: 'cell-form.name',
            tableColumns: [
                { field: 'id', label: this.$t('post.page.tablecolumnlabels.ref'), sortable: true, width: '80px' },
                { field: 'form.name', label: this.$t('post.page.tablecolumnlabels.form'), width: '12%' },
                { field: 'status_id', label: this.$t('post.page.tablecolumnlabels.status'), sortable: true, width: '13%' },
                { field: 'title', label: this.$t('post.page.tablecolumnlabels.subject'), sortable: true, width: '28%' },
                { field: 'created_at', label: this.$t('post.page.tablecolumnlabels.date'), sortable: true, width: '9%' },
                { field: 'operator', label: this.$t('post.page.tablecolumnlabels.operator'), width: '9%' },
                { field: 'priority', label: this.$t('post.page.tablecolumnlabels.priority'), sortable: true, width: '9%', align: 'center' },
                { field: 'actions', label: this.$t('post.page.tablecolumnlabels.shares'), width: '9%', align: 'center' }
            ]
        }
    },
    computed: {
        ...mapState('post', {
            posts: (state) => state.posts,
            config: (state) => state.postsConfig,
            post: (state) => state.post
        })
    },
    methods: {
        ...mapActions('post', ['getPosts', 'getPost']),
        getInitials(name) {
            if (!name) return '?'
            return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
        },

        showPostPreview(post) {
            this.getPost(post.id).then((result) => {
                this.setSelectedPost = result.data.post
            })
            this.$refs.postPreview.trigger()
        },
        loadAsyncData() {
            this.getPosts()
                .then(() => {
                    this.loading = false
                })
                .finally(() => {
                    this.loading = true
                })
        },
        loadpage() {
            this.loading = true
            this.$emit('loadpage')
        },
        onSort({ field, direction }) {
            this.config.page = 1
            this.config.sort = field
            this.config.sortDirection = direction
            this.loadAsyncData()
        },
        onPageChange(page) {
            this.config.page = page
            this.loadAsyncData()
        }
    }
}
</script>

<style scoped>
.bmv-action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
}

.bmv-action-btn i {
    font-size: 0.78rem;
}

.bmv-action-btn--view {
    background: #0891B2;
}

.tp-operators {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.tp-operator-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    background: rgba(8, 145, 178, 0.08);
    color: #0891B2;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
}

.tp-operator-badge i {
    font-size: 0.68rem;
}

.tp-operator-link {
    text-decoration: none;
    transition: all 0.15s ease;
}

.tp-operator-link:hover {
    background: rgba(8, 145, 178, 0.18);
    text-decoration: none;
    color: #0e7490;
}

.bmv-action-btn--view:hover {
    background: #0e7490;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(8, 145, 178, 0.3);
}
</style>
